// Baking a vertical flip into SVG path data.
//
// WHY THIS EXISTS. The brand mark's paths are authored bottom-up and rely on an
// SVG `transform="matrix(1,0,0,-1,0,H)"` to display right-side-up
// (`lib/brand.ts`). `pdf-lib`'s `drawSvgPath` takes path data and nothing else
// — no transform — so the flip has to be in the numbers before it gets there.
//
// It is NOT enough to let pdf-lib's own axis handling do it. pdf-lib emits
// `scale(1, -1)` around the path so that SVG's y-down convention lands
// correctly in PDF's y-up space, which means it renders DISPLAY coordinates.
// Our paths hold AUTHORING coordinates, one flip away from those. Passing them
// raw draws the mark upside down.
//
// SAFE FOR THIS ARTWORK AND NOT A GENERAL SVG PARSER. The brand paths use only
// `M`, `L` and `C`, all absolute, so every number run is a flat sequence of
// `x y` pairs and the flip is "negate each y, add the viewBox height". A path
// with relative commands, arcs (`A`) or the vertical/horizontal shorthands
// (`V`/`H`) would need real parsing; `flipSvgPathY` refuses those rather than
// mangling them silently.

/** Commands whose arguments are a flat run of `x y` pairs, absolute. */
const PAIRED_ABSOLUTE = /^[MLC]$/

/** Anything outside the supported set, which must not be silently mishandled. */
const UNSUPPORTED = /[mlcsqtavhSQTAVHzZ]/

const NUMBER = /-?\d*\.?\d+(?:[eE][-+]?\d+)?/g

/** Trim a flipped coordinate to the source's own precision, not a float tail. */
function trim(value: number): string {
  return String(Number(value.toFixed(5)))
}

/**
 * The same path, mirrored about the horizontal line `height / 2`.
 *
 * Throws on a path this cannot handle, because a mark drawn from a
 * half-understood path is worse than a missing one: it looks deliberate.
 */
export function flipSvgPathY(d: string, height: number): string {
  if (UNSUPPORTED.test(d)) {
    throw new Error('flipSvgPathY supports only absolute M, L and C commands')
  }
  return d.replace(/([MLC])([^MLC]*)/g, (_match, command: string, body: string) => {
    if (!PAIRED_ABSOLUTE.test(command)) return _match
    const numbers = body.match(NUMBER) ?? []
    if (numbers.length % 2 !== 0) {
      throw new Error(`flipSvgPathY found an odd coordinate run after ${command}`)
    }
    const flipped = numbers.map((value, index) =>
      index % 2 === 1 ? trim(height - Number(value)) : value,
    )
    return `${command}${flipped.join(' ')}`
  })
}
